interface ThemeTokens {
  tokens: Record<string, string | number>;
  tokensLayout: Record<string, string>;
  tokensTooltip: Record<string, string>;
  tokensTree: Record<string, string>;
}

interface ColorVariations {
  base: string;
  lighter: string;
  darker: string;
  transparent15: string;
  transparent08: string;
  transparent04: string;
  transparent02: string;
  transparent18: string;
  transparent12: string;
}

// Utility function to convert hex to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

// Utility function to convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

// Function to darken a color
function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = 1 - percent / 100;
  return rgbToHex(
    Math.round(rgb.r * factor),
    Math.round(rgb.g * factor),
    Math.round(rgb.b * factor)
  );
}

// Function to lighten a color
function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  return rgbToHex(
    Math.round(rgb.r + (255 - rgb.r) * factor),
    Math.round(rgb.g + (255 - rgb.g) * factor),
    Math.round(rgb.b + (255 - rgb.b) * factor)
  );
}

// Function to create color variations
function createColorVariations(color: string): ColorVariations {
  const rgb = hexToRgb(color);
  if (!rgb) throw new Error(`Invalid color format: ${color}`);

  return {
    base: color,
    lighter: lightenColor(color, 20),
    darker: darkenColor(color, 15),
    transparent15: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
    transparent08: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`,
    transparent04: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.04)`,
    transparent02: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.02)`,
    transparent18: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.18)`,
    transparent12: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
  };
}

export function generateThemeTokens(
  primaryColor: string,
  secondaryColor: string,
  accentColor?: string,
  theme: 'light' | 'dark' = 'light'
): ThemeTokens {
  // Create color variations
  const primary = createColorVariations(primaryColor);
  const secondary = createColorVariations(secondaryColor);
  const accent = accentColor ? createColorVariations(accentColor) : primary;

  // Generate tokens based on theme
  if (theme === 'light') {
    const tokens = {
      // Brand colors
      colorPrimary: primary.base,
      colorSuccess: secondary.lighter,
      colorWarning: accent.lighter,
      colorError: primary.darker,
      colorInfo: primary.base,

      // Background colors
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#fafafa',
      colorBgSpotlight: 'rgba(0, 0, 0, 0.85)',

      // Text colors
      colorText: '#212121',
      colorTextSecondary: 'rgba(33, 33, 33, 0.65)',
      colorTextTertiary: 'rgba(33, 33, 33, 0.45)',
      colorTextQuaternary: 'rgba(33, 33, 33, 0.25)',

      // Border colors
      colorBorder: '#e0e0e0',
      colorBorderSecondary: '#eeeeee',

      // Other UI elements
      colorFill: primary.transparent15,
      colorFillSecondary: primary.transparent08,
      colorFillTertiary: primary.transparent04,
      colorFillQuaternary: primary.transparent02,

      // Layout components
      bodyBg: '#ffffff',
      footerBg: '#fafafa',
      footerPadding: '24px 50px',
      headerBg: primary.base,
      headerColor: '#ffffff',
      headerHeight: 64,
      headerPadding: '0 50px',
      lightSiderBg: '#ffffff',
      lightTriggerBg: '#ffffff',
      lightTriggerColor: '#212121',
      siderBg: primary.base,
      triggerBg: primary.darker,
      triggerColor: '#ffffff',
      triggerHeight: 48,
      zeroTriggerHeight: 40,
      zeroTriggerWidth: 40,
    };

    const tokensLayout = {
      headerBg: '#ffffff',
      siderBg: '#ffffff',
      triggerBg: '#ffffff',
      footerBg: '#fafafa',
    };

    const tokensTooltip = {
      colorBgSpotlight: '#212121',
    };

    const tokensTree = {
      colorText: '#212121',
      colorTextSecondary: 'rgba(33, 33, 33, 0.65)',
      colorBgContainer: 'transparent',
    };

    return {
      tokens,
      tokensLayout,
      tokensTooltip,
      tokensTree,
    };
  } else {
    const tokens = {
      // Brand colors
      colorPrimary: primary.lighter,
      colorSuccess: secondary.base,
      colorWarning: accent.base,
      colorError: primary.base,
      colorInfo: primary.lighter,

      // Background colors
      colorBgContainer: '#212121',
      colorBgElevated: '#2a2a2a',
      colorBgLayout: '#212121',
      colorBgSpotlight: '#ffffff',

      // Text colors
      colorText: '#f5f5f5',
      colorTextSecondary: 'rgba(245, 245, 245, 0.65)',
      colorTextTertiary: 'rgba(245, 245, 245, 0.45)',
      colorTextQuaternary: 'rgba(245, 245, 245, 0.25)',

      // Border colors
      colorBorder: '#424242',
      colorBorderSecondary: '#333333',

      // Other UI elements
      colorFill: primary.transparent18,
      colorFillSecondary: primary.transparent12,
      colorFillTertiary: primary.transparent08,
      colorFillQuaternary: primary.transparent04,

      // Layout components
      bodyBg: '#212121',
      footerBg: '#2a2a2a',
      footerPadding: '24px 50px',
      headerBg: '#2a2a2a',
      headerColor: '#f5f5f5',
      headerHeight: 64,
      headerPadding: '0 50px',
      lightSiderBg: '#2a2a2a',
      lightTriggerBg: '#2a2a2a',
      lightTriggerColor: '#f5f5f5',
      siderBg: '#2a2a2a',
      triggerBg: '#333333',
      triggerColor: '#f5f5f5',
      triggerHeight: 48,
      zeroTriggerHeight: 40,
      zeroTriggerWidth: 40,
    };

    const tokensLayout = {
      headerBg: '#212121',
      siderBg: '#212121',
      triggerBg: '#2a2a2a',
      footerBg: '#212121',
    };

    const tokensTooltip = {
      colorBgSpotlight: '#2a2a2a',
    };

    const tokensTree = {
      colorText: '#f5f5f5',
      colorTextSecondary: 'rgba(245, 245, 245, 0.65)',
      colorBgContainer: 'transparent',
    };

    return {
      tokens,
      tokensLayout,
      tokensTooltip,
      tokensTree,
    };
  }
}
